import { supabase } from '@/integrations/supabase/client';

export const SHOPIFY_ADMIN_DOMAIN = 'hsq1yg-gg.myshopify.com';
export const SHOPIFY_ADMIN_URL = `https://admin.shopify.com/store/hsq1yg-gg`;

export async function shopifyAdminGraphQL<T = any>(
  query: string,
  variables: Record<string, unknown> = {},
): Promise<T> {
  const { data, error } = await supabase.functions.invoke('shopify-admin', {
    body: { query, variables },
  });
  if (error) throw new Error(error.message || 'Shopify admin call failed');
  if ((data as any)?.errors) {
    throw new Error(
      (data as any).errors.map((e: any) => e.message).join(', '),
    );
  }
  return (data as any).data as T;
}

// ===== Orders =====
export interface ShopifyOrder {
  id: string;
  name: string;
  createdAt: string;
  displayFinancialStatus: string | null;
  displayFulfillmentStatus: string | null;
  customer: { displayName: string; email: string | null; phone: string | null } | null;
  email: string | null;
  totalPriceSet: { shopMoney: { amount: string; currencyCode: string } };
  lineItems: {
    edges: Array<{
      node: { id: string; title: string; quantity: number; variantTitle: string | null };
    }>;
  };
  tags: string[];
}

const ORDERS_QUERY = `
  query Orders($first: Int!, $query: String) {
    orders(first: $first, query: $query, sortKey: CREATED_AT, reverse: true) {
      edges { node {
        id name createdAt displayFinancialStatus displayFulfillmentStatus
        email tags
        customer { displayName email phone }
        totalPriceSet { shopMoney { amount currencyCode } }
        lineItems(first: 20) { edges { node { id title quantity variantTitle } } }
      } }
    }
  }
`;

export async function fetchShopifyOrders(query = ''): Promise<ShopifyOrder[]> {
  const data = await shopifyAdminGraphQL<{
    orders: { edges: Array<{ node: ShopifyOrder }> };
  }>(ORDERS_QUERY, { first: 100, query });
  return data.orders.edges.map((e) => e.node);
}

export function shopifyOrderAdminUrl(orderGid: string): string {
  const id = orderGid.split('/').pop();
  return `${SHOPIFY_ADMIN_URL}/orders/${id}`;
}

// ===== Customers (for client merge) =====
export interface ShopifyCustomer {
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  numberOfOrders: string;
  amountSpent: { amount: string; currencyCode: string };
  createdAt: string;
}

const CUSTOMERS_QUERY = `
  query Customers($first: Int!) {
    customers(first: $first, sortKey: UPDATED_AT, reverse: true) {
      edges { node {
        id displayName email phone numberOfOrders createdAt
        amountSpent { amount currencyCode }
      } }
    }
  }
`;

export async function fetchShopifyCustomers(): Promise<ShopifyCustomer[]> {
  const data = await shopifyAdminGraphQL<{
    customers: { edges: Array<{ node: ShopifyCustomer }> };
  }>(CUSTOMERS_QUERY, { first: 250 });
  return data.customers.edges.map((e) => e.node);
}

// ===== Mark fulfilled =====
const FULFILL_QUERY = `
  query FOs($orderId: ID!) {
    order(id: $orderId) {
      fulfillmentOrders(first: 10) {
        edges { node { id status lineItems(first: 50) { edges { node { id remainingQuantity } } } } }
      }
    }
  }
`;

const FULFILLMENT_CREATE = `
  mutation Create($f: FulfillmentV2Input!) {
    fulfillmentCreateV2(fulfillment: $f) {
      fulfillment { id status }
      userErrors { field message }
    }
  }
`;

export async function markOrderFulfilled(orderGid: string): Promise<void> {
  const data = await shopifyAdminGraphQL<any>(FULFILL_QUERY, { orderId: orderGid });
  const fos = (data.order?.fulfillmentOrders?.edges ?? [])
    .map((e: any) => e.node)
    .filter((fo: any) => fo.status === 'OPEN' || fo.status === 'IN_PROGRESS');
  if (fos.length === 0) throw new Error('אין הזמנת מילוי פתוחה');
  const lineItemsByFulfillmentOrder = fos.map((fo: any) => ({
    fulfillmentOrderId: fo.id,
    fulfillmentOrderLineItems: fo.lineItems.edges
      .filter((e: any) => e.node.remainingQuantity > 0)
      .map((e: any) => ({ id: e.node.id, quantity: e.node.remainingQuantity })),
  }));
  const res = await shopifyAdminGraphQL<any>(FULFILLMENT_CREATE, {
    f: { lineItemsByFulfillmentOrder, notifyCustomer: true },
  });
  const errs = res.fulfillmentCreateV2?.userErrors ?? [];
  if (errs.length) throw new Error(errs.map((e: any) => e.message).join(', '));
}

// ===== Draft Order + invoice =====
const DRAFT_ORDER_CREATE = `
  mutation DraftCreate($input: DraftOrderInput!) {
    draftOrderCreate(input: $input) {
      draftOrder { id name invoiceUrl }
      userErrors { field message }
    }
  }
`;

const DRAFT_ORDER_INVOICE = `
  mutation Invoice($id: ID!) {
    draftOrderInvoiceSend(id: $id) {
      draftOrder { id name invoiceUrl }
      userErrors { field message }
    }
  }
`;

export interface DraftOrderLineItem {
  title: string;
  quantity: number;
  originalUnitPrice: string; // amount as string
}

export async function createDraftOrderWithInvoice(opts: {
  email: string;
  customerName?: string;
  phone?: string;
  lineItems: DraftOrderLineItem[];
  shippingAmount?: number; // in shop currency, 0 = none
  shippingTitle?: string;
  note?: string;
  tags?: string[];
}): Promise<{ id: string; name: string; invoiceUrl: string }> {
  const input: any = {
    email: opts.email,
    note: opts.note ?? 'רוקחות אישית',
    tags: opts.tags ?? ['personal-rx'],
    lineItems: opts.lineItems.map((li) => ({
      title: li.title,
      quantity: li.quantity,
      originalUnitPrice: li.originalUnitPrice,
    })),
  };
  if (opts.shippingAmount && opts.shippingAmount > 0) {
    input.shippingLine = {
      title: opts.shippingTitle ?? 'משלוח',
      price: opts.shippingAmount.toFixed(2),
    };
  }

  const created = await shopifyAdminGraphQL<any>(DRAFT_ORDER_CREATE, { input });
  const errs = created.draftOrderCreate?.userErrors ?? [];
  if (errs.length) throw new Error(errs.map((e: any) => e.message).join(', '));
  const draft = created.draftOrderCreate.draftOrder;

  const invoiced = await shopifyAdminGraphQL<any>(DRAFT_ORDER_INVOICE, { id: draft.id });
  const errs2 = invoiced.draftOrderInvoiceSend?.userErrors ?? [];
  if (errs2.length) throw new Error(errs2.map((e: any) => e.message).join(', '));
  const sent = invoiced.draftOrderInvoiceSend.draftOrder;
  return { id: sent.id, name: sent.name, invoiceUrl: sent.invoiceUrl };
}
